<template>
  <div>
    <h1>
      {{ true != null ? $t("settings.users.userEdit") : $t("settings.users.userNew") }}
    </h1>

    <div class="mt-3 elevation-12 grey lighten-4">
      <user-settings-menu />

      <v-card>
        <v-card-text>
          <h2>{{ $t("settings.users.hasPermissions") }}</h2>

          <v-data-table
            :headers="headersAssignedPerms"
            :items="authItems"
            hide-default-footer
            :footer-props="{ 'items-per-page-options': [{ text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }] }"
          >
            <template #item="props">
              <tr>
                <td class="text-left">
                  {{ props.item.type == 2 ? $t("global.permission") : $t("global.role") }}
                </td>
                <td class="text-left">{{ props.item.item_name }}</td>
                <td class="text-left">
                  <v-tooltip top
                    >{{ props.item.from_date }} - {{ props.item.to_date }} {{ $t("global.by") }} {{ props.item.deputy_by_name }}
                    <template #activator="{on}">
                      <span v-on="on"
                        >{{ props.item.deputy_by_id != null ? $t("global.yes") : $t("global.no") }}
                        <v-icon style="font-size: 16px" v-if="props.item.deputy_by_id != null">info</v-icon>
                      </span>
                    </template>
                  </v-tooltip>
                </td>
                <td class="text-left">{{ props.item.item_description }}</td>
                <td class="text-left">
                  <v-tooltip bottom>
                    <template #activator="{on}">
                      <v-btn :disabled="$store.getters['Ajax/loading']" @click.native="removeAuthItem(props.item)" icon v-on="on">
                        <v-icon>clear</v-icon>
                      </v-btn>
                    </template>
                    {{ $t("global.delete") }}
                  </v-tooltip>
                </td>
              </tr>
            </template>
            <template #no-data>
              <v-alert color="error" icon="warning">
                {{ $t("settings.users.noPermission") }}
              </v-alert>
            </template>
          </v-data-table>

          <v-row class="mt-5 md-0" style="margin-bottom: 15px">
            <v-col
              ><h3>{{ $t("settings.users.addPermissions") }}</h3></v-col
            >
            <v-spacer />
            <v-col cols="3">
              <v-text-field :label="$t('global.search')" append-icon="search" hide-details single-line v-model="search" />
            </v-col>
          </v-row>

          <v-data-table :headers="headers" :items="availableAuthItems" :search="search">
            <template #item="props">
              <tr>
                <td class="text-left">
                  {{ props.item.type == 2 ? $t("global.permission") : $t("global.role") }}
                </td>
                <td class="text-left">{{ props.item.translated_name }}</td>
                <td class="text-left">{{ props.item.description }}</td>
                <td class="text-left">
                  <v-tooltip bottom>
                    {{ $t("global.add") }}
                    <template #activator="{on}">
                      <v-btn :disabled="$store.getters['Ajax/loading']" @click.native="addAuthItem(props.item)" icon v-on="on">
                        <v-icon>add</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-card>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click.native="cancel()" color="blue darken-1" text>{{ $t("global.cancel") }}</v-btn>
          <v-btn :disabled="$store.getters['Ajax/loading']" @click.native="save()" color="blue darken-1" text>{{ $t("global.save") }} </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
import UserSettingsMenu from "./UserSettingsMenu";

export default {
  name: "UserDelegationPermissionsDataForm",
  components: { UserSettingsMenu },
  data: () => ({
    authItems: [],
    allAuthItems: [],
    search: null,
    formChanged: false
  }),
  created() {
    this.axios.get("settings/user/available-auth-items").then(resp => {
      this.allAuthItems = resp.data;
    });
    this.axios
      .get("settings/user/get-auth-data", {
        params: { userId: this.$route.params.id }
      })
      .then(resp => {
        this.authItems = resp.data;
      });
  },
  methods: {
    filterAlreadyHaveItems(item) {
      for (let userAuthItem of this.authItems) {
        if (userAuthItem.item_name === item.name && userAuthItem.deputy_by_id == null) {
          return false;
        }
      }
      return true;
    },
    addAuthItem(item) {
      this.authItems.push({
        id: null,
        item_name: item.name,
        item_description: item.description,
        comment: null,
        deputy_by_id: null,
        deputy_by_name: null,
        from_date: null,
        to_date: null
      });
      this.formChanged = true;
    },
    removeAuthItem(item) {
      this.authItems.splice(this.authItems.indexOf(item), 1);
      this.formChanged = true;
    },
    save() {
      this.axios
        .post("settings/user/save-auth-data", {
          userId: this.$route.params.id,
          authItems: this.authItems
        })
        .then(() => {
          this.$store.dispatch("snackbarPush", {
            text: this.$t("global.saved"),
            position: "down"
          });
          this.formChanged = false;
        });
    }
  },
  computed: {
    headers() {
      return [
        { text: this.$t("settings.users.type"), value: "type", width: "5%" },
        { text: this.$t("settings.users.name"), value: "name", width: "10%" },
        {
          text: this.$t("settings.users.description"),
          value: "description",
          width: "80%"
        },
        {
          text: this.$t("global.actions"),
          value: "actions",
          sortable: false,
          width: "5%"
        }
      ];
    },
    headersAssignedPerms() {
      return [
        { text: this.$t("settings.users.type"), value: "type", width: "5%" },
        { text: this.$t("settings.users.name"), value: "name", width: "10%" },
        {
          text: this.$t("settings.users.deputyAuth"),
          value: "name",
          width: "10%"
        },
        {
          text: this.$t("settings.users.description"),
          value: "description",
          width: "70%"
        },
        {
          text: this.$t("global.actions"),
          value: "actions",
          sortable: false,
          width: "5%"
        }
      ];
    },
    availableAuthItems() {
      const array = [];
      for (let authItem of this.allAuthItems.filter(this.filterAlreadyHaveItems)) {
        array.push(authItem);
      }
      return array;
    }
  },
  beforeRouteLeave(from, to, next) {
    if (this.formChanged) {
      let result = this.$store.dispatch("confirmDialog", {
        content: this.$t("global.saveBeforeExit.content"),
        title: this.$t("global.saveBeforeExit.title"),
        customButtons: [
          { text: this.$t("global.cancel"), value: "cancel" },
          { text: this.$t("global.no"), value: "no" },
          { text: this.$t("global.yes"), color: "primary", value: "yes" }
        ]
      });
      result.then(chosen => {
        if (chosen === "cancel") {
          next(false);
        } else if (chosen === "no") {
          next();
        } else if (chosen === "yes") {
          this.axios
            .post("settings/user/save-auth-data", {
              userId: this.$route.params.id,
              authItems: this.authItems
            })
            .then(() => {
              this.$store.dispatch("snackbarPush", {
                text: this.$t("global.saved"),
                position: "down"
              });
              next();
            });
        }
      });
    } else {
      next();
    }
  }
};
</script>

<style scoped></style>
